import React, { useState, useCallback, useEffect } from "react";
import { Tabs, Row, Col, Avatar, Card, Button, message } from "antd";
import { useLocation } from "react-router-dom";
import { useHMSActions, useAVToggle } from "@100mslive/react-sdk";
import { UserOutlined } from "@ant-design/icons";
import "./SummaryStyle.css";
import DoctorNotesForm from "../common/doctorNotesForm/DoctorNotesForm";
import MedicalTab from "./MedicalTab";
import VitalDataTab from "./VitalDataTab";
import DocumentsTab from "./DocumentsTab";
import EcgTab from "./EcgTab";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CommonModal from "../common/commonModal/CommonModal";
import SummaryTab from "./SummaryTab";
import DeviceSection from "../common/device/DeviceSection";
import { API_URL } from "../../config";
import { useTranslation } from "react-i18next";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const SummaryContainer = React.memo(function SummaryContainer({
  children,
  patientDetails,
}) {
  const hmsActions = useHMSActions();
  const { t } = useTranslation();
  const { loginDoctorDetails, setGlobalLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const token = location?.state?.doctorVideoCallToken;
  const appointmentId = location?.state?.appointmentId;
  const doctorId = location?.state?.doctorId;
  const patient_id = location?.state?.rowData?.patient_id;
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [showSummary, setShowSummary] = useState(false);
  const [showTabContentPage, setShowTabContentPage] = useState(false);
  const [showVitalSigns, setShowVitalSigns] = useState(false);
  const [deviceStates, setDeviceStates] = useState({
    BP: "Start",
    Thermometer: "Start",
    SPO2: "Start",
    WeightHeight: "Start",
    ECG: "Start",
  });
  const [profileImageUrl, setProfileImageUrl] = useState("");

  const DeviceGroupId = {
    WeightHeight: 1,
    Body: 2,
    Thermometer: 3,
    FatPercentage: 2,
    BP: 4,
    SPO2: 5,
    ECG: 6,
    Stethoscope: 7,
    Otoscope: 10,
    NGSP: 11,
    CH: 12,
    BG: 13,
  };


  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/user/download-patient-picture/${patient_id}`,
        );
        setProfileImageUrl(response.data.url);
      } catch (error) {
        console.error("Error fetching profile image:", error);
      }
    };
    fetchProfileImage();
  }, []);

  const handleDeviceAction = (deviceGroup, event) => {
    const params = {
      device_group: deviceGroup,
      doctor_id: doctorId,
      appointment_id: appointmentId,
      event,
    };

    axios
      .post(`${API_URL}/appointment/doctor_event/`, params)
      .catch((error) => {
        if (error.response?.status === 409) {
          message.error("Device Not found");
        } else {
          console.error(error);
        }
      });
  };

  const handleStartDevice = (deviceGroupId) => {
    handleDeviceAction(deviceGroupId, "DEVICE");
  };

  const handleStopDevice = (deviceGroupId) => {
    handleDeviceAction(deviceGroupId, "STOP_DEVICE");
  };

  const toggleDeviceState = (device) => {
    setDeviceStates((prevState) => ({
      ...prevState,
      [device]: prevState[device] === "Start" ? "Stop" : "Start",
    }));

    if (deviceStates[device] === "Start") {
      handleStartDevice(DeviceGroupId[device]);
    } else {
      handleStopDevice(DeviceGroupId[device]);
    }
  };

  const onChange = useCallback(
    debounce((key) => {
      setActiveTab(key);
      setShowSummary(false);
      setShowTabContentPage(false);
    }, 300),
    [],
  );

  const handleEndSession = () => {
    setShowEndSessionModal(true);
  };

  const handleCancelEndSession = () => {
    setShowEndSessionModal(false);
  };

  const handleConfirmEndSession = async () => {
    try {
      setGlobalLoading(true);
      const lock = false;
      await hmsActions.leave();
      await hmsActions.endRoom(lock);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const { doctor_id } = loginDoctorDetails;
      const data = {
        doctor_id,
        appointment_id: appointmentId,
        event: "END",
        device_group: 0,
      };
      const headers = {
        "Content-Type": "application/json",
      };

      try {
        const response = await axios.post(
          `${API_URL}/appointment/doctor_event/`,
          data,
          { headers },
        );

        if (response.status === 200) {
          navigate("/appointments");
          setTimeout(() => {
            window.location.reload();
          }, 50);
          setShowEndSessionModal(false);
        } else {
          console.error("Unexpected response status:", response.status);
        }
      } catch (error) {
        console.error("Error ending session:", error);
      }
    } catch (error) {
      console.error("Error leaving session:", error);
    } finally {
      setGlobalLoading(false);
    }
  };

  const items = [
    {
      key: "1",
      label: t("patientPage.videoConsultation"),
    },
    {
      key: "2",
      label: t("patientPage.medical"),
    },
    {
      key: "3",
      label: t("patientPage.vitalData"),
    },
    {
      key: "4",
      label: t("patientPage.documents"),
    },
    {
      key: "5",
      label: t("patientPage.ecg"),
    },
  ];

  const handleTabContentPageClick = () => {
    setShowTabContentPage(true);
    setShowSummary(true);
  };

  const handleVideoCall = () => {
    setActiveTab("1");
    setShowSummary(false);
    setShowTabContentPage(false);
    setShowVitalSigns(true);
  };

  const filteredItems = patientDetails?.tablet_id
    ? items.filter((item) => item.key === "1")
    : items;

  return (
    <React.Fragment>
      <div className="tabs-container">
        <Row justify="space-around">
          {filteredItems.map((item) => (
            <Col key={item.key}>
              <span
                onClick={() => onChange(item.key)}
                className={`text-button ${activeTab === item.key ? "active-tab" : ""
                  }`}
                style={{ cursor: "pointer", fontWeight: "bold" }}
              >
                {item.label}
              </span>
            </Col>
          ))}
        </Row>

        <hr
          style={{
            borderColor: "#F5F5F5",
            height: "2px",
            background: "#F5F5F5",
            border: "red",
          }}
        />
      </div>
      {activeTab === "1" && (
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Card>
              {showTabContentPage ? (
                <SummaryTab
                  appointment_id={appointmentId}
                  patientDetails={patientDetails}
                />
              ) : (
                children
              )}
              <DoctorNotesForm
                appointment_id={appointmentId}
                loginDoctorDetails={loginDoctorDetails}
              />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <div className="patient-info">
                <Avatar
                  size={64}
                  icon={!profileImageUrl && <UserOutlined />}
                  src={profileImageUrl || null}
                />
                <div className="patient-details">
                  <h3>
                    {patientDetails?.patient_details?.first_name 
                      ? `${patientDetails?.patient_details?.first_name} ${patientDetails?.patient_details?.last_name}`
                      : "-"}
                  </h3>
                  <span>
                    {t("tableHeader.gender")}:
                    {patientDetails?.patient_details?.gender
                      ? patientDetails?.patient_details?.gender
                      : "-"}
                  </span>
                  <span>
                    {t("tableHeader.age")}:
                    {patientDetails?.patient_details?.age
                      ? patientDetails?.patient_details?.age
                      : "-"}
                  </span>
                </div>
              </div>
            </Card>
            {!patientDetails?.mobile &&
              !patientDetails?.tablet_id &&
              !showTabContentPage ? (
              <div className="vital-signs">
                <DeviceSection
                  deviceStates={deviceStates}
                  toggleDeviceState={toggleDeviceState}
                  appointmentId={appointmentId}
                />
              </div>
            ) : null}
            {showTabContentPage && children}
            {patientDetails?.mobile && patientDetails?.tablet_id && (
              <DoctorNotesForm
                appointment_id={appointmentId}
                loginDoctorDetails={loginDoctorDetails}
              />
            )}
            <Row justify="center" gutter={[16, 16]} style={{ marginTop: 20 }}>
              <Col span={12} className="button-margin">
                <Button
                  type="primary"
                  size="large"
                  style={{ width: "100%" }}
                  onClick={
                    showTabContentPage
                      ? handleVideoCall
                      : handleTabContentPageClick
                  }
                >
                  {showTabContentPage ? `${t('patientPage.video')}` : `${t('patientPage.showSummary')}`}
                </Button>
              </Col>
              <Col span={12} className="button-margin">
                <Button
                  type="primary"
                  danger
                  size="large"
                  style={{ width: "100%" }}
                  onClick={handleEndSession}
                >
                  {t('patientPage.endSession')}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {activeTab !== "1" && (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Col xs={24} md={24}>
              {!showSummary && activeTab === "2" && (
                <MedicalTab
                  patient_id={patient_id}
                  appointmentId={appointmentId}
                  patientDetails={patientDetails}
                />
              )}
              {!showSummary && activeTab === "3" && (
                <VitalDataTab patient_id={patient_id} />
              )}
              {!showSummary && activeTab === "4" && (
                <DocumentsTab
                  patient_id={patient_id}
                  patientDetails={patientDetails}
                />
              )}
              {!showSummary && activeTab === "5" && (
                <EcgTab
                  patient_id={patient_id}
                  appointment_id={appointmentId}
                />
              )}
              {showSummary && (
                <SummaryTab
                  appointment_id={appointmentId}
                  patient_id={patient_id}
                  patientDetails={patientDetails}
                />
              )}
            </Col>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <div className="patient-info">
                <Avatar
                  size={64}
                  icon={!profileImageUrl && <UserOutlined />}
                  src={profileImageUrl || null}
                />
                <div className="patient-details">
                  <h3>
                    {patientDetails?.patient_details?.first_name 
                      ? `${patientDetails?.patient_details?.first_name} ${patientDetails?.patient_details?.last_name}`
                      : "-"}
                  </h3>
                  <span>
                    {t('tableHeader.gender')} :{" "}
                    {patientDetails?.patient_details?.gender
                      ? patientDetails?.patient_details?.gender
                      : "-"}
                  </span>
                  <span>
                    {t('tableHeader.age')} :{" "}
                    {patientDetails?.patient_details?.age
                      ? patientDetails?.patient_details?.age
                      : "-"}
                  </span>
                </div>
              </div>
            </Card>
            <Row>{children}</Row>
            <DoctorNotesForm
              appointment_id={appointmentId}
              loginDoctorDetails={loginDoctorDetails}
            />
            <Row justify="center" gutter={[16, 16]} style={{ marginTop: 20 }}>
              <Col span={12} className="button-margin">
                <Button
                  type="primary"
                  size="large"
                  style={{ width: "100%" }}
                  onClick={
                    showTabContentPage
                      ? handleVideoCall
                      : handleTabContentPageClick
                  }
                >
                  {showTabContentPage ? `${t('patientPage.video')}` : `${t('patientPage.showSummary')}`}
                </Button>
              </Col>
              <Col span={12} className="button-margin">
                <Button
                  type="primary"
                  danger
                  size="large"
                  style={{ width: "100%" }}
                  onClick={handleEndSession}
                >
                  {t('patientPage.endSession')}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {showEndSessionModal && (
        <CommonModal
          title={t('patientPage.endSession')}
          visible={showEndSessionModal}
          onCancel={handleCancelEndSession}
          onConfirm={handleConfirmEndSession}
          confirmButtonText={t('patientPage.leave')}
          confirmButtonType="primary"
          confirmButtonDanger={true}
        >
          <p>{t('patientPage.endConversation')}</p>
        </CommonModal>
      )}
    </React.Fragment>
  );
});

export default SummaryContainer;
